import { useState } from 'react';
import type { Product, StockMovement, StockMovementKind } from '@salonomy/types';
import { Button, Card, EmptyState, Field, Pill, Select, Spinner, TextInput } from '@salonomy/ui';
import type { PillTone } from '@salonomy/ui';
import { useStockMovements, useSuppliers } from '../../data/stock.js';
import { useSalon } from '../../data/salon.js';
import { money } from '../../lib/format.js';
import { longDate } from '../../lib/time.js';
import { MovementModal } from './MovementModal.js';
import './stock.css';

const KIND_FILTERS: Array<{ value: '' | StockMovementKind; label: string }> = [
  { value: '', label: 'All kinds' },
  { value: 'receive', label: 'Receive' },
  { value: 'sell', label: 'Sell' },
  { value: 'waste', label: 'Waste' },
  { value: 'count_correction', label: 'Count correction' },
  { value: 'return', label: 'Return' },
];

const KIND_LABEL: Record<StockMovementKind, string> = {
  receive: 'Receive',
  sell: 'Sell',
  waste: 'Waste',
  count_correction: 'Correction',
  return: 'Return',
};

const KIND_TONE: Record<StockMovementKind, PillTone> = {
  receive: 'success',
  sell: 'cobalt',
  waste: 'danger',
  count_correction: 'neutral',
  return: 'soft',
};

/**
 * The append-only movement LEDGER — READ-ONLY. Every row is a posting; there is no edit or delete
 * (a correction is recorded as a NEW count_correction via the Record button → MovementModal). Paged +
 * filterable by product / kind / date window. `qtyDelta` is the server's signed value, shown with its
 * sign; on-hand is never reconstructed here from the rows. 'sell' rows (posted by the till) are
 * read-only here too and offered as a filter, but the create form can't post one.
 */
export function MovementsPanel({ products }: { products: Product[] }) {
  const { data: salon } = useSalon();
  const { data: suppliers } = useSuppliers();
  const tz = salon?.timezone ?? 'Europe/London';

  const [productId, setProductId] = useState('');
  const [kind, setKind] = useState<'' | StockMovementKind>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [recordOpen, setRecordOpen] = useState(false);

  const { data, isLoading, isError, isFetching } = useStockMovements(
    {
      productId: productId || undefined,
      kind: kind || undefined,
      // The query wants ISO datetimes; a date input gives YYYY-MM-DD, so widen to whole-day bounds.
      from: from ? `${from}T00:00:00.000Z` : undefined,
      to: to ? `${to}T23:59:59.999Z` : undefined,
    },
    offset,
  );

  const rows = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const limit = data?.pagination.limit ?? 50;
  const productName = (id: string) => products.find((p) => p.id === id)?.name ?? '—';

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setOffset(0);
    };
  }

  return (
    <Card>
      <div className="stock-head">
        <div>
          <div className="section-title" style={{ marginBottom: 2 }}>
            Movements
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            The append-only stock ledger. Every receive, waste, correction, return and sale.
          </div>
        </div>
        <Button size="sm" onClick={() => setRecordOpen(true)}>
          Record movement
        </Button>
      </div>

      <div className="stock-filters">
        <div className="stock-filters__grow">
          <Field label="Product">
            <Select value={productId} onChange={(e) => resetPage(setProductId)(e.target.value)}>
              <option value="">All products</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div style={{ minWidth: 150 }}>
          <Field label="Kind">
            <Select
              value={kind}
              onChange={(e) => resetPage(setKind)(e.target.value as '' | StockMovementKind)}
            >
              {KIND_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="From">
          <TextInput
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => resetPage(setFrom)(e.target.value)}
          />
        </Field>
        <Field label="To">
          <TextInput
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => resetPage(setTo)(e.target.value)}
          />
        </Field>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading the ledger…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load the movement ledger. Try again.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No movements yet"
          description="Receives, waste, corrections and returns land here. Record one to start the ledger."
          action={
            <Button size="sm" onClick={() => setRecordOpen(true)}>
              Record movement
            </Button>
          }
        />
      ) : (
        <div
          className="stock-table"
          style={{ ['--cols' as string]: '1fr minmax(0,1.6fr) 1fr 0.7fr minmax(0,1.4fr)' }}
        >
          <div className="stock-row stock-row--head">
            <span>Date</span>
            <span>Product</span>
            <span>Kind</span>
            <span className="stock-num">Qty</span>
            <span>Reason</span>
          </div>
          {rows.map((m: StockMovement) => (
            <div key={m.id} className="stock-row">
              <span className="muted">{longDate(m.createdAt.slice(0, 10), tz)}</span>
              <span className="stock-cell-main__name">{productName(m.productId)}</span>
              <span>
                <Pill tone={KIND_TONE[m.kind]}>{KIND_LABEL[m.kind]}</Pill>
              </span>
              <span className="stock-num mono">
                {m.qtyDelta > 0 ? `+${m.qtyDelta}` : m.qtyDelta}
              </span>
              <span className="muted" style={{ fontSize: 12.5 }}>
                {m.reason ?? (m.unitCostPence != null ? `@ ${money(m.unitCostPence)}/unit` : '—')}
              </span>
            </div>
          ))}
        </div>
      )}

      {total > limit ? (
        <div className="stock-pager">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || isFetching}
            onClick={() => setOffset((o) => Math.max(0, o - limit))}
          >
            ← Newer
          </Button>
          <span className="muted" style={{ fontSize: 12.5 }}>
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + limit >= total || isFetching}
            onClick={() => setOffset((o) => o + limit)}
          >
            Older →
          </Button>
        </div>
      ) : null}

      {recordOpen ? (
        <MovementModal
          products={products}
          suppliers={suppliers ?? []}
          defaultProductId={productId || undefined}
          onClose={() => setRecordOpen(false)}
        />
      ) : null}
    </Card>
  );
}
